import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import type { MicroAppResponse } from "@/api";
import { getMicroAppQueryKey, listMicroAppsQueryKey, schemas, updateMicroAppMutation } from "@/api";
import { EntityFormCard, formText, IdentityFields } from "@/components/build/EntityForm";
import { useEntitySave } from "@/components/build/useEntitySave";
import { useAppForm } from "@/components/form/form";

const update = schemas.zMicroAppUpdate.shape;
const microAppFormSchema = z.object({
  name: update.name.unwrap().unwrap(),
  description: update.description,
});
type MicroAppFormValues = z.infer<typeof microAppFormSchema>;

/** Overview tab: name + description (content is generated HTML, republished from chat). */
export function MicroAppOverviewForm({ app }: Readonly<{ app: MicroAppResponse }>) {
  const { t } = useTranslation("microApps");
  const save = useMutation(updateMicroAppMutation());
  const text = formText(t);
  const { saveExisting } = useEntitySave(text.messages);

  const form = useAppForm({
    defaultValues: { name: app.name, description: app.description ?? "" } as MicroAppFormValues,
    validators: { onChange: microAppFormSchema },
    onSubmit: ({ value }) =>
      saveExisting({
        request: () =>
          save.mutateAsync({
            path: { app_uuid: app.uuid },
            body: { name: value.name, description: value.description || null },
            throwOnError: true,
          }),
        invalidate: [listMicroAppsQueryKey(), getMicroAppQueryKey({ path: { app_uuid: app.uuid } })],
        onSaved: () => form.reset(value),
      }),
  });

  return (
    <EntityFormCard label={t("form.label")} onSubmit={() => void form.handleSubmit()}>
      <IdentityFields
        form={form}
        fields={{ name: "name", description: "description" }}
        descriptionRows={3}
        labels={text.labels}
      />
      <form.AppForm>
        <form.SubmitActions onCancel={() => form.reset()} />
      </form.AppForm>
    </EntityFormCard>
  );
}
